<template>
  <v-layout row wrap>
    <v-flex :lg5="modified" :lg6="!modified" pr-2>
      <v-autocomplete
        label="Country"
        v-model="profile.countryOfBirth"
        :color="themes.candidate.light.input"
        :items="countries"
        item-text="name"
        item-value="code"
        :error-messages="countryErrorMessages"
        required
        :rules="rules.countryOfBirth"
        :menu-props="{ offsetY: true }"
        attach
      />
    </v-flex>
    <v-flex :lg5="modified" :lg6="!modified">
      <v-text-field
        label="National identification (or passport) number"
        v-model="profile.identification"
        :color="themes.candidate.light.input"
        :mask="masks[profile.countryOfBirth]"
        :error-messages="identificationErrorMessages"
        required
        :rules="rules.identification"
      />
    </v-flex>
    <v-flex xs12 v-if="submissionError" class="error--text caption mt-2">
      {{ submissionError }}
    </v-flex>
    <v-flex xs12 lg2 v-if="modified" class="text-xs-right">
      <v-btn
        :color="themes.candidate.light.primaryActionButton"
        :loading="loading"
        @click="submit"
        class="mx-0"
      >
        Apply change
      </v-btn>
    </v-flex>
  </v-layout>
</template>

<script>
// Data
import countries from "@/data/countries";

// Helpers
import { LuhnCheck } from "@/mixins/LuhnCheck";
import { mapGetters } from "vuex";

// Vue Component
export default {
  mixins: [LuhnCheck],
  props: {
    countries: {
      type: Array,
      default() {
        return countries;
      },
    },
    masks: {
      default() {
        return {
          ZA: "###### #### ###",
          ZW: "## ###### A##",
          US: "###-##-####",
        };
      },
    },
    value: Object,
  },
  data() {
    return {
      // Loading indicators
      loading: false,
      uploading: false,
      submissionError: "",
      // Model (object sent to API)
      profile: {
        countryOfBirth: this.value.countryOfBirth,
        identification: this.value.identification,
      },
      identityDocument: this.value.identityDocument,
      rules: {
        countryOfBirth: [(v) => !!v || "You must specify a country of origin"],
        identification: [
          (v) => !!v || "You must specify a method of identification",
          (v) =>
            this.profile.countryOfBirth != "ZA" ||
            this.LuhnCheck(v) ||
            "You must specify a valid identification",
        ],
      },
    };
  },
  computed: {
    modified() {
      return (
        this.value.countryOfBirth !== this.profile.countryOfBirth ||
        this.value.identification !== this.profile.identification
      );
    },
    countryErrorMessages() {
      return this.getFieldErrors(this.value.errors, [
        "country_of_birth",
        "countryOfBirth",
      ]);
    },
    identificationErrorMessages() {
      return this.getFieldErrors(this.value.errors, ["identification"]);
    },
  },
  methods: {
    normalizeErrorMessages(errorValue) {
      if (Array.isArray(errorValue)) return errorValue.filter(Boolean);
      if (typeof errorValue === "string" && errorValue) return [errorValue];
      return [];
    },
    getFieldErrors(errors, fields) {
      if (!errors) return [];

      return fields.reduce((messages, field) => {
        return messages.concat(this.normalizeErrorMessages(errors[field]));
      }, []);
    },
    formatFieldName(field) {
      return field
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (char) => char.toUpperCase());
    },
    findNonIdentityError(errors) {
      if (!errors) return "";

      const identityFields = [
        "identification",
        "country_of_birth",
        "countryOfBirth",
      ];
      const keys = Object.keys(errors).filter(
        (key) => !identityFields.includes(key)
      );

      for (const key of keys) {
        const messages = this.normalizeErrorMessages(errors[key]);
        if (messages.length > 0) {
          return `${this.formatFieldName(key)} ${messages[0]}`;
        }
      }

      return "";
    },
    applyProfileUpdateError(error) {
      const responseErrors =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.errors) ||
        this.value.errors;

      const identityErrors = this.getFieldErrors(responseErrors, [
        "identification",
        "country_of_birth",
        "countryOfBirth",
      ]);
      if (identityErrors.length > 0) {
        this.submissionError = "";
        return;
      }

      const otherError = this.findNonIdentityError(responseErrors);
      if (otherError) {
        this.submissionError = otherError;
        return;
      }

      if (this.value.errors) {
        this.value.errors.identification =
          "Please ensure your identification number is typed correctly or does not already exist on our system";
      }
      this.submissionError = "";
    },
    submit() {
      if (this.modified) {
        this.loading = true;
        this.submissionError = "";
        this.$store.dispatch("candidate/profile/update", this.profile).then(
          () => {
            this.loading = false;
            this.$emit("input");
          },
          (error) => {
            this.loading = false;
            this.applyProfileUpdateError(error);
          }
        );
      }
    },
  },
};
</script>
